'use client'

import { useInfiniteQuery } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'

import { predictionEventsInfiniteQueryOptions } from '@/services/prediction/events'
import type {
  PredictionEvent,
  PredictionEventsPage,
  PredictionEventsParams,
} from '@/services/prediction/types'

/**
 * 展示页卡数（桌面两列 Grid → 偶数保持整行，用户拍板 10）。
 * 数据层不再按此值 limit——每块拉 FETCH_BLOCK_SIZE 条入池，展示层按
 * 10 条/页从池里切窗口（见 ensureVisibleCount）。
 */
export const PREDICTION_PAGE_SIZE = 10

/**
 * 数据层每块拉取条数（= 5 个展示页）。拉大块、前端整合：池内覆盖当前页
 * 前后几次翻页时零请求、纯前端切页，只有翻出池外才沿 cursor 补块（「一次
 * 不用按照实际的分页数量来 limit，只是前端展示控制 10 条/页」——WWW 拍板）。
 * 50 条同时也够消化掉过期 recurring 过滤掉的冗余。
 */
const FETCH_BLOCK_SIZE = 50

/** ensure 循环的保险丝（极端：接口持续吐数据但一半被过滤的情况）。 */
const MAX_LOOP_GUARD = 64

export interface PagedPredictionEventsOptions {
  /**
   * 可见性判定（Port of cdc-web `BaseLeagueSection` 的 `filteredData`）：
   * 已过期 recurring 事件不进池、不占页。数据层只认「可见数量是否凑满一页」，
   * 过滤规则由调用方注入，hook 不感知业务细节。
   */
  isVisible: (event: PredictionEvent) => boolean
  /** 展示页条数。默认 PREDICTION_PAGE_SIZE。 */
  pageSize?: number
}

/**
 * 页码式翻页（累积池模型）：
 *
 * - 数据获取是一层：RQ `useInfiniteQuery` 按块（FETCH_BLOCK_SIZE 条）拉事件
 *   进池，翻页/轮询都只保证「池里可见卡凑满目标页 * pageSize，或后端到底」。
 * - 展示是一层：`records` = 池内可见事件按 pageSize 切出的当前页窗口。
 *
 * 与旧「每页 = 接口一页」的模型差异：接口页（块）与展示页解耦，过滤（过期
 * recurring 剔除）不再导致展示页不满——翻到某页时自动沿 cursor 拉块，直到
 * 该页凑满 pageSize 张可见卡或 has_more=false（「凑满一页或拉取到底才结束」）。
 *
 * RQ 缓存里保留已拉的全部池数据，翻回旧页是瞬时的；每块事件只有
 * FETCH_BLOCK_SIZE 条，比 loadMore 的「全量 append + 全量渲染」轻。
 */
export function usePagedPredictionEvents(
  params: PredictionEventsParams,
  options: PagedPredictionEventsOptions,
): UsePagedPredictionEventsResult {
  const { isVisible, pageSize = PREDICTION_PAGE_SIZE } = options
  const query = useInfiniteQuery(predictionEventsInfiniteQueryOptions(params, FETCH_BLOCK_SIZE))
  const [currentPage, setCurrentPage] = useState(1)

  // 切 L1 / L2 / sort → queryKey 变，RQ 新查询从第 1 块开始，窗口同步归位。
  useEffect(() => {
    setCurrentPage(1)
  }, [params.navNode?.id ?? 'all', params.sortBy])

  // 单飞 guard：狂点时只保留一个 ensure 循环在跑，后续调用提升 target
  // 并复用同一个 promise（照抄 Stock useCursorPagedStocks 的成熟套路）。
  const inFlightRef = useRef<Promise<void> | null>(null)
  /** 开启中的目标可见条数（只增不减；循环条件读它，实现「连点提升目标」）。 */
  const targetVisibleRef = useRef(0)

  // 蒙层状态：任何 gotoPage 的 fetch 链结束前保持亮。连点页码时用引用计数
  // 保护——前一个 goto 的 finally 不会提前扒掉后一个 goto 的蒙层。
  const [isNavigating, setIsNavigating] = useState(false)
  const pendingNavCountRef = useRef(0)

  /** 池内可见事件总数（每个 render 从 RQ 快照现算）。 */
  const visiblePoolCount =
    query.data?.pages.flatMap(page => page.data).filter(isVisible).length ?? 0

  /** 能撑起的展示页数上限（尾页不满时会有小数，上游取整用）。 */
  const visiblePageCount = Math.ceil(visiblePoolCount / pageSize)

  /**
   * 沿 cursor 链拉块，直到「池内可见 ≥ target 条」或「fetchNextPage 不再
   * 推进页数（has_more=false，到底）」。
   * 用「块数是否推进」判断是否到底——query.data 快照在 await 后陈旧，
   * 不能信它的 hasNextPage 状态。
   */
  const ensureVisibleCount = (targetVisible: number): Promise<void> => {
    // 提升目标：连点页码时后发的高目标覆盖在跑的低目标（循环条件读 ref，
    // 同一个进行中的循环朝最新目标拉，而不是被堵在第一个调用上）。
    targetVisibleRef.current = Math.max(targetVisibleRef.current, targetVisible)
    if (inFlightRef.current !== null) return inFlightRef.current

    const run = async (): Promise<void> => {
      // fetchNextPage 至少能返回 InfiniteData（`pages`）或 observer result
      // （`data.pages`，视 RQ 版本的类型形状而定），运行时两者都取 pages。
      const fetchNextPages = async (): Promise<PredictionEventsPage[]> => {
        const res: { pages?: PredictionEventsPage[]; data?: { pages: PredictionEventsPage[] } } =
          (await query.fetchNextPage()) as never
        return res.pages ?? res.data?.pages ?? []
      }

      const countVisible = (pages: PredictionEventsPage[]) =>
        pages.flatMap(page => page.data).filter(isVisible).length

      // query.data 是定义本轮 render 的快照，fetchNextPage resolve 后仍旧，
      // 不能靠它判断「这一块有没有让可见数涨上去」。改用 fetchNextPage 返回
      // 的最新 pages 现算，凑满目标立即停——否则点一次页码会连锁把整个
      // 列表拉到底（auto-fill effect 会在每块回来时再用新目标重触发）。
      let guard = 0
      while (guard < MAX_LOOP_GUARD) {
        const pages = query.data?.pages ?? []
        if (countVisible(pages) >= targetVisibleRef.current) return // 已凑满目标
        const next = await fetchNextPages()
        if (next.length === pages.length) return // 无新块 = 已到底
        if (countVisible(next) >= targetVisibleRef.current) return // 这块已凑满
        guard += 1
      }
    }

    const promise = run()
    inFlightRef.current = promise
    promise.finally(() => {
      if (inFlightRef.current === promise) inFlightRef.current = null
      // 目标只给「进行中的循环」用；循环结束清零，避免陈目标污染下轮。
      targetVisibleRef.current = 0
    })
    return promise
  }

  // 兜底自动凑页：数据就绪但当前页不满（过滤后可见不足 / 轮询后滚动），
  // 自动补齐或拉到底。轮询刷新带来的「列出发变化 → 当前页不满」也走这里。
  useEffect(() => {
    if (!query.data) return
    void ensureVisibleCount(currentPage * pageSize)
  }, [currentPage, pageSize, query.data])

  const gotoPage = async (targetPage: number) => {
    if (targetPage < 1 || targetPage === currentPage) return
    if (targetPage <= visiblePageCount || !query.data) {
      // 池内可见已能撑起目标页 → 瞬时切换（不拉新块）。
      setCurrentPage(targetPage)
      return
    }
    // 池内可见不足目标页 → 沿 cursor 链拉块凑页，只成功才切。
    // 期间 isNavigating=true 亮蒙层（fetch 链结束才撤）。
    pendingNavCountRef.current += 1
    setIsNavigating(true)
    try {
      const targetVisible = targetPage * pageSize
      await ensureVisibleCount(targetVisible)
      // ensure 可能提前到底——把落点 clamp 到池内可见能撑起的真实页数，
      // 避免停在「没有数据」的页上（跟旧版 min(target, loadedPages) 同语义）。
      const actualVisiblePages = Math.ceil(
        (query.data?.pages.flatMap(page => page.data).filter(isVisible).length ?? 0) / pageSize,
      )
      setCurrentPage(Math.max(1, Math.min(targetPage, Math.max(1, actualVisiblePages))))
    } finally {
      pendingNavCountRef.current -= 1
      if (pendingNavCountRef.current === 0) setIsNavigating(false)
    }
  }

  /**
   * 池内任意展示页的窗口切片（currentPage 由 hook 管理，展示层可随意取
   * 其它页——「contracts 就绪后再原子切页」的场景，见 index.tsx）。
   * 只读、无副作用；每 render 现算（slice 10 条，廉价）。
   */
  const recordsAt = (page: number): PredictionEvent[] => {
    const pool = query.data?.pages.flatMap(page => page.data).filter(isVisible) ?? []
    return pool.slice((page - 1) * pageSize, page * pageSize)
  }

  // 当前页窗口 = 池内可见事件按 pageSize 切的第 currentPage 片。
  const records = recordsAt(currentPage)

  // 分页器可见页码上限：池内可见撑起的页数；当前页恒可见（ensure 已凑
  // 满或到底），所以至少是 currentPage。
  const loadedPageCount = Math.max(currentPage, visiblePageCount)

  return {
    recordsAt,
    records,
    isPending: query.isPending,
    isFetching: query.isFetchingNextPage,
    hasMore: query.hasNextPage,
    loadedPageCount,
    currentPage,
    isNavigating,
    gotoPage,
  }
}

interface UsePagedPredictionEventsResult {
  /** 池内任意展示页的窗口（展示层择机取用；见 recordsAt 注释）。 */
  recordsAt: (page: number) => PredictionEvent[]
  /** 当前展示页的事件（已过滤 isVisible、已按 pageSize 切片）。 */
  records: PredictionEvent[]
  /** True on the very first load (no data yet). */
  isPending: boolean
  /** True whenever any chunk fetch is in flight (initial or prefetch). */
  isFetching: boolean
  /** True while a page-switch fetch chain is running (drives the loading overlay). */
  isNavigating: boolean
  /** True while the backend can still return more chunks. */
  hasMore: boolean
  /** Number of display pages the pool can back (= pagination's loadedPageCount). */
  loadedPageCount: number
  /** 1-indexed current page. */
  currentPage: number
  gotoPage: (page: number) => void | Promise<void>
}
